import React, { useEffect, useRef, useState } from "react";
import * as Mui from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import * as Icons from "react-feather";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { DialogFrameLayout, DialogFrameShell } from "component/shared/dialog-frame";
import { OutlinedButton } from "component/shared/outlined-button";
import { clampStep, isFirstStep, isLastStep, resolveStep, stepCounter } from "./wizard-steps";
import { styles } from "./style";

/**
 * Handed to a step's content so it can move the wizard itself, e.g. a "Review Step 2" link.
 * `next` and `goTo` move unconditionally: they bypass the current step's `canContinue` and
 * `onContinue`, which only guard the Continue button.
 */
export interface WizardNav {
	index: number;
	/** Jump to a step by index or key. An unknown key is ignored. */
	goTo: (step: number | string) => void;
	next: () => void;
	back: () => void;
}

/**
 * One step. `steps` is read on every render, so pass fresh values (e.g. `canContinue` derived
 * from current form state) rather than a memoized array.
 */
export interface WizardStep {
	key: string;
	/** Heading under the counter, e.g. "Add the Script to Your Website". */
	title?: string;
	/** Intro copy under the title, spaced with the heading rather than the step's content. */
	description?: React.ReactNode;
	/** Defaults to true. False for a preamble (e.g. Select Platform) that the "Step n of N" counter skips. */
	counted?: boolean;
	content: React.ReactNode | ((nav: WizardNav) => React.ReactNode);
	/** Defaults to true. False disables Continue (and Complete Setup on the last step). */
	canContinue?: boolean;
	/**
	 * Runs before advancing, with the buttons disabled and the dialog uncloseable until it settles.
	 * - Return (or resolve) `false` to stay on the step silently, e.g. after showing your own inline validation.
	 * - Throw (or reject) to stay on the step with the wizard's error message, e.g. when a save fails.
	 */
	onContinue?: () => boolean | void | Promise<boolean | void>;
	/** Overrides the primary button label for this step. */
	continueLabel?: string;
}

interface Props {
	open: boolean;
	/** Modal title, e.g. "Set Up IDX". */
	title: string;
	steps: WizardStep[];
	onClose: () => void;
	/**
	 * Runs from the last step's primary button, after that step's own onContinue. Throwing (or
	 * rejecting) keeps the wizard open on the last step with the error message.
	 */
	onComplete: () => void | Promise<void>;
	/** Primary label on the last step. Every first-release wizard ends at Complete Setup (D1). */
	completeLabel?: string;
	/** When set, the first step shows a secondary button with this label that closes the wizard. */
	cancelLabel?: string;
	/** Shown above the buttons when an onContinue or onComplete throws. */
	errorMessage?: string;
	/**
	 * Where the wizard opens, by index or key. Defaults to the first step. Read each time the
	 * wizard opens; exact-step resume will feed this once progress is saved server-side (SPFF-7).
	 */
	initialStep?: number | string;
	/** Called whenever the visible step changes, including the step the wizard opens on. */
	onStepChange?: (index: number, key: string) => void;
}

/**
 * Shared modal stepper for the IDX setup wizards (MAX-4419 VPLAT 3, 15, 19): title and close,
 * an optional "Step n of N" counter, the step's content, and Back / Continue. The last step's
 * primary button is Complete Setup. Steps can jump anywhere through the WizardNav they receive.
 */
export const WizardDialog: React.FC<Props> = (props) => {
	const { open, onClose } = props;
	const busy = useRef(false);
	const titleId = useRef(`wizard-dialog-title-${Math.random().toString(36).slice(2)}`).current;
	// A close request while a step is saving is ignored, so onComplete can't land on a dismissed wizard.
	const handleClose = () => {
		if (!busy.current) {
			onClose();
		}
	};
	// The body is a child of the Dialog, which unmounts it on close; each opening therefore starts
	// fresh at initialStep, and onStepChange never reports the previous opening's step.
	return (
		<DialogFrameShell open={open} onClose={handleClose} titleId={titleId} width={840} disableBackdropClick>
			<WizardBody {...props} onClose={handleClose} titleId={titleId} busyRef={busy} />
		</DialogFrameShell>
	);
};

interface BodyProps extends Props, WithStyles<typeof styles> {
	titleId: string;
	busyRef: React.MutableRefObject<boolean>;
}

const WizardBodyComponent: React.FC<BodyProps> = ({
	classes = {},
	title,
	steps,
	onClose,
	onComplete,
	completeLabel = "Complete Setup",
	cancelLabel,
	errorMessage = "Something went wrong. Please try again.",
	initialStep = 0,
	onStepChange,
	titleId,
	busyRef,
}) => {
	const [index, setIndex] = useState(() => resolveStep(steps, initialStep) ?? 0);
	const [busy, setBusyState] = useState(false);
	const [failed, setFailed] = useState(false);
	const setBusy = (value: boolean) => {
		busyRef.current = value;
		setBusyState(value);
	};

	const current = clampStep(index, steps.length);
	const step = steps[current];

	useEffect(() => {
		if (step) {
			onStepChange?.(current, step.key);
		}
		// Reported once per visible step, not on every re-render with a fresh callback.
	}, [current, step?.key]); // eslint-disable-line

	// A closed wizard never leaves the ref stuck on a save that outlived the body.
	useEffect(() => () => {
		busyRef.current = false;
	}, [busyRef]);

	if (!step) {
		return null;
	}

	const goTo = (ref: number | string) => {
		const target = resolveStep(steps, ref);
		if (target !== undefined) {
			setFailed(false);
			setIndex(target);
		}
	};
	const nav: WizardNav = {
		index: current,
		goTo,
		next: () => goTo(current + 1),
		back: () => goTo(current - 1),
	};

	const last = isLastStep(current, steps.length);
	const counter = stepCounter(steps, current);

	const handleContinue = async () => {
		if (busyRef.current) {
			return;
		}
		setBusy(true);
		setFailed(false);
		try {
			if (step.onContinue && (await step.onContinue()) === false) {
				setBusy(false);
				return;
			}
			if (last) {
				await onComplete();
			}
			setBusy(false);
			if (!last) {
				nav.next();
			}
		} catch{
			setBusy(false);
			setFailed(true);
		}
	};

	return (
		<DialogFrameLayout
			onClose={onClose}
			title={title}
			titleId={titleId}
			closeDisabled={busy}
			notice={failed && <MuiAlert severity="error">{errorMessage}</MuiAlert>}
			actions={
				<>
					{!isFirstStep(current) ? (
						<OutlinedButton
							startIcon={<Icons.ArrowLeft size={16} />}
							disabled={busy}
							onClick={nav.back}
						>
							Back
						</OutlinedButton>
					) : cancelLabel && (
						<OutlinedButton disabled={busy} onClick={onClose}>
							{cancelLabel}
						</OutlinedButton>
					)}
					<Mui.Button
						variant="contained"
						color="primary"
						endIcon={last ? undefined : <Icons.ArrowRight size={16} />}
						disabled={busy || step.canContinue === false}
						onClick={handleContinue}
					>
						{step.continueLabel ?? (last ? completeLabel : "Continue")}
					</Mui.Button>
				</>
			}
		>
			<div className={classes.content}>
				{(counter || step.title || step.description) && (
					<div className={classes.stepHeading}>
						{counter && (
							<Mui.Typography className={classes.counter}>
								Step {counter.current} of {counter.total}
							</Mui.Typography>
						)}
						{step.title && (
							<Mui.Typography component="h3" className={classes.stepTitle}>
								{step.title}
							</Mui.Typography>
						)}
						{step.description}
					</div>
				)}
				{typeof step.content === "function" ? step.content(nav) : step.content}
			</div>
		</DialogFrameLayout>
	);
};

const WizardBody = withStyles(WizardBodyComponent, styles);
